// src/common/components/Theme-Editor/ThemeEditor.tsx
import React, { useCallback, useState } from 'react';
import { Form, Input, Button, ColorPicker, Slider, Space, Typography, Divider, Switch, Select, Collapse, Upload } from 'antd';
import { useThemeStore } from '@/app/store/themeStore';
import type { Color } from 'antd/es/color-picker';
import type { ThemeConfig } from 'antd';
import { aiThemePresets } from '@/common/constants/aiThemePresets';
import { theme as AntdTheme } from 'antd'; // Import AntdTheme for darkAlgorithm
import { InputEditor, TableEditor, CardEditor, SelectEditor, DatePickerEditor, SwitchEditor, InputNumberEditor } from './ComponentEditors';
import ComponentPropertyEditor from './ComponentPropertyEditor';
import { UploadOutlined } from '@ant-design/icons';
import { usePalette } from 'color-thief-react';
import type { UploadChangeParam } from 'antd/es/upload';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const fontFamilyOptions = [
    { label: 'System UI (default)', value: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'` },
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Roboto', value: 'Roboto, sans-serif' },
    { label: 'Segoe UI', value: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
    { label: 'Helvetica Neue', value: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
];

const componentOptions = [
    { label: 'Container', value: 'Container' },
    { label: 'Input', value: 'Input' },
    { label: 'InputNumber', value: 'InputNumber' },
    { label: 'Table', value: 'Table' },
    { label: 'Card', value: 'Card' },
    { label: 'Select', value: 'Select' },
    { label: 'DatePicker', value: 'DatePicker' },
    { label: 'Switch', value: 'Switch' },
];

const industryOptions = [
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Finance', value: 'finance' },
    { label: 'Technology', value: 'technology' },
    { label: 'Education', value: 'education' },
    { label: 'Retail', value: 'retail' },
    { label: 'Manufacturing', value: 'manufacturing' },
    { label: 'Government', value: 'government' },
    { label: 'Non-profit', value: 'nonprofit' },
];
const uiFrameStyleOptions = [
    { label: 'Tailwind UI', value: 'tailwindUi' },
    { label: 'Shadcn UI', value: 'shadcnUi' },
    { label: 'Tailwind', value: 'tailwind' },
    { label: 'Heroui', value: 'heroui' },
    { label: 'Material UI', value: 'materialUi' },
    { label: 'Bootstrap', value: 'bootstrap' },
    { label: 'Chakra UI', value: 'chakraUi' },
    { label: 'Semantic UI', value: 'semanticUi' },
    { label: 'Foundation', value: 'foundation' },
    { label: 'Bulma', value: 'bulma' },
    { label: 'Fluent UI', value: 'fluentUi' },
    { label: 'NextUI', value: 'nextUi' },
    { label: 'Mantine', value: 'mantine' },
    { label: 'Ant Design', value: 'antDesign' },
    { label: 'PrimeReact', value: 'primeReact' },
    { label: 'Rebass', value: 'rebass' },
    { label: 'Grommet', value: 'grommet' },
];

// aiThemePresets will be imported from a separate file

const ImageColorPalette = ({ imageUrl, onColorSelect, onGenerateTheme }: { imageUrl: string, onColorSelect: (color: string) => void, onGenerateTheme: (palette: string[]) => void }) => {
    const { data, loading } = usePalette(imageUrl, 8, 'hex', { crossOrigin: 'anonymous' });

    if (loading) return <div>Loading...</div>;

    return (
        <>
            <Space wrap style={{ marginTop: 20 }}>
                {data?.map((color, index) => (
                    <Space key={index} direction="vertical" align="center">
                        <div
                            onClick={() => onColorSelect(color)}
                            style={{
                                width: 32,
                                height: 32,
                                backgroundColor: color,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: '2px solid #fff',
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                            }}
                            title={color}
                        />
                        <Text copyable={{ text: color }} style={{ fontSize: 12 }}>{color}</Text>
                    </Space>
                ))}
            </Space>
            {data && (
                <Button onClick={() => onGenerateTheme(data)} style={{ marginTop: 20 }}>
                    Generate Full Theme
                </Button>
            )}
        </>
    );
};


function ThemeEditor() {
    const { themeConfig, setThemeConfig, resetTheme, setMode, mode } = useThemeStore();
    const [form] = Form.useForm();
    const [selectedComponent, setSelectedComponent] = useState('Container');
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);


    // Update form fields when themeConfig changes (e.g., on initial load from localStorage)
    React.useEffect(() => {
        form.setFieldsValue({
            colorPrimary: themeConfig.token?.colorPrimary,
            borderRadius: themeConfig.token?.borderRadius,
            fontFamily: themeConfig.token?.fontFamily,
            colorSuccess: themeConfig.token?.colorSuccess,
            colorWarning: themeConfig.token?.colorWarning,
            colorError: themeConfig.token?.colorError,
            colorInfo: themeConfig.token?.colorInfo,
            colorLink: themeConfig.token?.colorLink,
            colorLinkHover: themeConfig.token?.colorLinkHover,
            colorLinkActive: themeConfig.token?.colorLinkActive,
            colorTextBase: themeConfig.token?.colorTextBase,
            colorBgBase: themeConfig.token?.colorBgBase,
            fontSize: themeConfig.token?.fontSize,
            sizeStep: themeConfig.token?.sizeStep,
            sizeUnit: themeConfig.token?.sizeUnit,
            isDarkMode: themeConfig.algorithm === AntdTheme.darkAlgorithm,
        });
    }, [themeConfig, form, mode]);

    const handleColorChange = useCallback((newColor: Color, key: keyof NonNullable<ThemeConfig['token']>) => {
        const newThemeConfig: ThemeConfig = {
            ...themeConfig,
            token: {
                ...themeConfig.token,
                [key]: typeof newColor === 'string' ? newColor : newColor.toHexString(),
            },
        };
        setThemeConfig(newThemeConfig);
    }, [themeConfig, setThemeConfig]);

    const handleSliderChange = useCallback((newValue: number | string, key: keyof NonNullable<ThemeConfig['token']>) => {
        const parsedValue = typeof newValue === 'string' ? parseFloat(newValue) : newValue;

        // Special handling for borderRadius: also update components overrides for Input and Button
        if (key === 'borderRadius') {
            setThemeConfig({
                ...themeConfig,
                token: {
                    ...themeConfig.token,
                    borderRadius: parsedValue,
                },
                components: {
                    ...themeConfig.components,
                    Button: { borderRadius: parsedValue },
                    Input: { borderRadius: parsedValue },
                },
            });
            return;
        }

        const newThemeConfig: ThemeConfig = {
            ...themeConfig,
            token: {
                ...themeConfig.token,
                [key]: parsedValue,
            },
        };
        setThemeConfig(newThemeConfig);
    }, [themeConfig, setThemeConfig]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, key: keyof NonNullable<ThemeConfig['token']>) => {
        const value = e.target.value;

        // For borderRadius (which should be a number), convert to number and update components override
        if (key === 'borderRadius') {
            const parsedValue = parseFloat(value) || 0;
            setThemeConfig({
                ...themeConfig,
                token: {
                    ...themeConfig.token,
                    borderRadius: parsedValue,
                },
                components: {
                    ...themeConfig.components,
                    Button: { borderRadius: parsedValue },
                    Input: { borderRadius: parsedValue },
                },
            });
            return;
        }

        const newThemeConfig: ThemeConfig = {
            ...themeConfig,
            token: {
                ...themeConfig.token,
                [key]: value,
            },
        };
        setThemeConfig(newThemeConfig);
    }, [themeConfig, setThemeConfig]);

    const handleDarkModeChange = useCallback(
        (checked: boolean) => {
            setMode(checked ? 'dark' : 'light');
        },
        [setMode]


    );

    const handleComponentPropertyChange = (
        componentName: string,
        property: string,
        value: unknown
    ) => {
        setThemeConfig({
            ...themeConfig,
            components: {
                ...themeConfig.components,
                [componentName]: {
                    ...(themeConfig.components?.[componentName as keyof ThemeConfig['components']] || {}),
                    [property]: value,
                },
            },
        });
    };

    const handleApplyPreset = useCallback((value: string) => {
        const preset = aiThemePresets[value];
        if (preset) {
            setThemeConfig({
                ...preset,
                algorithm: mode === 'dark' ? AntdTheme.darkAlgorithm : AntdTheme.defaultAlgorithm,
            });
            form.setFieldsValue({
                colorPrimary: preset.token?.colorPrimary,
                borderRadius: preset.token?.borderRadius,
                fontFamily: preset.token?.fontFamily,
                colorSuccess: preset.token?.colorSuccess,
                colorWarning: preset.token?.colorWarning,
                colorError: preset.token?.colorError,
                colorInfo: preset.token?.colorInfo,
                colorLink: preset.token?.colorLink,
                colorLinkHover: preset.token?.colorLinkHover,
                colorLinkActive: preset.token?.colorLinkActive,
                colorTextBase: preset.token?.colorTextBase,
                colorBgBase: preset.token?.colorBgBase,
                fontSize: preset.token?.fontSize,
                sizeStep: preset.token?.sizeStep,
                sizeUnit: preset.token?.sizeUnit,
                isDarkMode: preset.algorithm === AntdTheme.darkAlgorithm,
            });
        }
    }, [setThemeConfig, form, mode]);

    const renderComponentEditor = () => {
        const props = {
            componentName: selectedComponent,
            properties: themeConfig.components?.[selectedComponent as keyof ThemeConfig['components']] || {},
            onPropertyChange: handleComponentPropertyChange,
        };

        switch (selectedComponent) {
            case 'Input':
                return <InputEditor {...props} />;
            case 'InputNumber':
                return <InputNumberEditor {...props} />;
            case 'Table':
                return <TableEditor {...props} />;
            case 'Card':
                return <CardEditor {...props} />;
            case 'Select':
                return <SelectEditor {...props} />;
            case 'DatePicker':
                return <DatePickerEditor {...props} />;
            case 'Switch':
                return <SwitchEditor {...props} />;
            case 'Container':
                return <ComponentPropertyEditor {...props} />;
            default:
                return null;
        }
    };

    const handleImageUpload = (info: UploadChangeParam) => {
        if (info.file.status === 'done') {
            const reader = new FileReader();
            reader.addEventListener('load', () => setUploadedImageUrl(reader.result as string));
            reader.readAsDataURL(info.file.originFileObj as Blob);
        }
    };

    const handleGenerateTheme = (palette: string[]) => {
        const newTheme: ThemeConfig = {
            ...themeConfig,
            token: {
                ...themeConfig.token,
                colorPrimary: palette[0],
                colorSuccess: palette[1],
                colorWarning: palette[2],
                colorError: palette[3],
                colorInfo: palette[4],
                colorLink: palette[5],
                colorTextBase: mode === 'dark' ? '#ffffff' : '#000000',
                colorBgBase: mode === 'dark' ? '#141414' : '#ffffff',
            },
        };
        setThemeConfig(newTheme);
        //console.log('Generated Palette:', palette);
        //console.log('New Theme Config:', newTheme);
    };


    return (
        <Form form={form} layout="vertical">
            <Title level={4}>Theme Settings</Title>
            <Text type="secondary">Customize your application's look and feel.</Text>

            <Divider />

            <Collapse defaultActiveKey={['1']}>
                <Panel header="Global Settings" key="1">
                    {/* Dark Mode Switch */}
                    <Form.Item label="Dark Mode" valuePropName="checked">
                        <Switch
                            style={{ zIndex: 9999 }}
                            checked={mode === 'dark'}
                            onChange={handleDarkModeChange}
                        />
                    </Form.Item>
                    <Divider />
                    <Title level={5}>Quick Theme Colors</Title>
                    <Space wrap>
                        {[
                            '#1890ff', // blue
                            '#52c41a', // green
                            '#fa8c16', // orange
                            '#f5222d', // red
                            '#722ed1', // purple
                            '#13c2c2', // cyan
                            '#eb2f96', // magenta
                            '#fa541c', // volcano
                        ].map((color) => (
                            <div
                                key={color}
                                onClick={() =>
                                    handleColorChange({ toHexString: () => color } as Color, 'colorPrimary')
                                }
                                style={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: color,
                                    borderRadius: '50%',
                                    border: '2px solid #fff',
                                    boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                                    cursor: 'pointer',
                                }}
                                title={color}
                            />
                        ))}
                    </Space>
                    <Divider />
                    <Title level={5}>Colors</Title>

                    {/* Primary Color */}
                    <Form.Item label="Primary Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorPrimary || '#1890ff'}
                                onChange={(color) => handleColorChange(color, 'colorPrimary')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorPrimary || '#1890ff'}
                                onChange={(e) => handleInputChange(e, 'colorPrimary')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Success Color */}
                    <Form.Item label="Success Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorSuccess || '#52c41a'}
                                onChange={(color) => handleColorChange(color, 'colorSuccess')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorSuccess || '#52c41a'}
                                onChange={(e) => handleInputChange(e, 'colorSuccess')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Warning Color */}
                    <Form.Item label="Warning Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorWarning || '#faad14'}
                                onChange={(color) => handleColorChange(color, 'colorWarning')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorWarning || '#faad14'}
                                onChange={(e) => handleInputChange(e, 'colorWarning')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Error Color */}
                    <Form.Item label="Error Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorError || '#ff4d4f'}
                                onChange={(color) => handleColorChange(color, 'colorError')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorError || '#ff4d4f'}
                                onChange={(e) => handleInputChange(e, 'colorError')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Info Color */}
                    <Form.Item label="Info Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorInfo || '#1890ff'}
                                onChange={(color) => handleColorChange(color, 'colorInfo')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorInfo || '#1890ff'}
                                onChange={(e) => handleInputChange(e, 'colorInfo')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Link Colors</Title>

                    {/* Link Color */}
                    <Form.Item label="Link Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorLink || '#1890ff'}
                                onChange={(color) => handleColorChange(color, 'colorLink')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorLink || '#1890ff'}
                                onChange={(e) => handleInputChange(e, 'colorLink')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Link Hover Color */}
                    <Form.Item label="Link Hover Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorLinkHover || '#69b1ff'}
                                onChange={(color) => handleColorChange(color, 'colorLinkHover')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorLinkHover || '#69b1ff'}
                                onChange={(e) => handleInputChange(e, 'colorLinkHover')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Link Active Color */}
                    <Form.Item label="Link Active Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorLinkActive || '#0958d9'}
                                onChange={(color) => handleColorChange(color, 'colorLinkActive')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorLinkActive || '#0958d9'}
                                onChange={(e) => handleInputChange(e, 'colorLinkActive')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Neutral Colors</Title>

                    {/* Text Base Color */}
                    <Form.Item label="Text Base Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorTextBase || '#000'}
                                onChange={(color) => handleColorChange(color, 'colorTextBase')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorTextBase || '#000'}
                                onChange={(e) => handleInputChange(e, 'colorTextBase')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    {/* Background Base Color */}
                    <Form.Item label="Background Base Color">
                        <Space>
                            <ColorPicker
                                value={themeConfig.token?.colorBgBase || '#fff'}
                                onChange={(color) => handleColorChange(color, 'colorBgBase')}
                                showText
                            />
                            <Input
                                value={themeConfig.token?.colorBgBase || '#fff'}
                                onChange={(e) => handleInputChange(e, 'colorBgBase')}
                                style={{ width: 120 }}
                            />
                        </Space>
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Size & Font</Title>
                    <Form.Item label="Font Family">
                        <Select
                            options={fontFamilyOptions}
                            value={themeConfig.token?.fontFamily}
                            onChange={(value) => {
                                const newThemeConfig = {
                                    ...themeConfig,
                                    token: {
                                        ...themeConfig.token,
                                        fontFamily: value,
                                    },
                                };
                                setThemeConfig(newThemeConfig);
                            }}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    {/* Font Size */}
                    <Form.Item label="Font Size">
                        <Slider
                            min={10}
                            max={20}
                            value={themeConfig.token?.fontSize || 14}
                            onChange={(value) => handleSliderChange(value, 'fontSize')}
                        />
                        <Input
                            value={themeConfig.token?.fontSize || 14}
                            onChange={(e) => handleInputChange(e, 'fontSize')}
                            style={{ width: 80 }}
                        />
                    </Form.Item>

                    {/* Spacing Step */}
                    {/* <Form.Item label="Spacing Step">
                <Slider
                min={1}
                max={10}
                value={themeConfig.token?.sizeStep || 4}
                onChange={(value) => handleSliderChange(value, 'sizeStep')}
                />
                <Input
                value={themeConfig.token?.sizeStep || 4}
                onChange={(e) => handleInputChange(e, 'sizeStep')}
                style={{ width: 80 }}
                />
            </Form.Item> */}

                    {/* Spacing Unit */}
                    {/* <Form.Item label="Spacing Unit">
                <Slider
                min={1}
                max={10}
                value={themeConfig.token?.sizeUnit || 4}
                onChange={(value) => handleSliderChange(value, 'sizeUnit')}
                />
                <Input
                value={themeConfig.token?.sizeUnit || 4}
                onChange={(e) => handleInputChange(e, 'sizeUnit')}
                style={{ width: 80 }}
                />
            </Form.Item> */}

                    <Divider />
                    <Title level={5}>Style</Title>

                    {/* Border Radius */}
                    <Form.Item label="Border Radius">
                        <Slider
                            min={0}
                            max={20}
                            value={themeConfig.token?.borderRadius || 6}
                            onChange={(value) => handleSliderChange(value, 'borderRadius')}
                        />
                        <Input
                            value={themeConfig.token?.borderRadius || 6}
                            onChange={(e) => handleInputChange(e, 'borderRadius')}
                            style={{ width: 80 }}
                        />
                    </Form.Item>
                </Panel>
                <Panel header="AI Theme Suggestions" key="3">
                    <Title level={5}>Pre-defined AI Themes</Title>
                    <Form.Item label="Select AI Theme">
                        <Select
                            options={[
                                // ...Object.keys(aiThemePresets).map((presetName) => ({
                                //     label: presetName.replace(/([A-Z])/g, ' $1').trim(),
                                //     value: presetName,
                                // })),
                                ...industryOptions,
                            ]}
                            onChange={handleApplyPreset}
                            placeholder="Select a theme preset or industry"
                        />
                    </Form.Item>
                    <Form.Item label="Select UI Frame Style">
                        <Select
                            options={uiFrameStyleOptions}
                            onChange={handleApplyPreset}
                            placeholder="Select a UI frame style"
                        />
                    </Form.Item>
                    <Divider />
                    <Title level={5}>Generate Theme from Image</Title>
                    <Upload
                        accept="image/*"
                        showUploadList={false}
                        customRequest={({ onSuccess }) => {
                            if (onSuccess) {
                                setTimeout(() => {
                                    onSuccess('ok');
                                }, 0);
                            }
                        }}
                        onChange={handleImageUpload}
                    >
                        <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                    {uploadedImageUrl && (
                        <ImageColorPalette
                            imageUrl={uploadedImageUrl}
                            onColorSelect={(color) => handleColorChange({ toHexString: () => color } as Color, 'colorPrimary')}
                            onGenerateTheme={handleGenerateTheme}
                        />
                    )}
                </Panel>
                <Panel header="Component Customization" key="2">
                    <Form.Item label="Select Component">
                        <Select
                            options={componentOptions}
                            value={selectedComponent}
                            onChange={setSelectedComponent}
                        />
                    </Form.Item>

                    {renderComponentEditor()}
                </Panel>
            </Collapse>

            <Divider />

            <Form.Item>
                <Button onClick={resetTheme} block>
                    Reset to Default
                </Button>
            </Form.Item>
        </Form>
    );
}

export default ThemeEditor;
